import axios from 'axios';
import { getViewMode } from './viewMode';
import { API_BASE_URL } from './api';

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  // Send the httpOnly session cookie the server sets on login. The API token
  // is kept in React state (not localStorage) for XSS hardening, so the cookie
  // is what actually authenticates most requests. In production frontend and
  // backend are same-origin (app.terramind.gr) so cookies flow either way;
  // this matters for LOCAL split-origin dev (React :3000 → Django :8000),
  // where without it the cross-origin cookie is dropped and every request 401s
  // — which blocked running the two dev servers from a fresh machine. Safe in
  // prod (same-origin) and CORS already sets CORS_ALLOW_CREDENTIALS = True.
  withCredentials: true,
});

// Attach auth token + language preference to every request
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('terraMindToken');
  if (token) {
    config.headers.Authorization = `Token ${token}`;
  }
  const lang = localStorage.getItem('terraMindLanguage') || 'el';
  config.headers['Accept-Language'] = lang;
  // The simple view: the backend answers as for a farmer (config/viewMode.js).
  if (getViewMode() === 'farmer') config.headers['X-TerraMind-View'] = 'farmer';
  return config;
});

// 401 handling — debounced, race-safe.
//
// Why this exists: F48 in UX_REMEDIATION_PLAN_2026-04-25. The user reports
// being randomly logged out. Two known causes:
//   1. Multiple in-flight requests racing on token expiry — each one used
//      to set window.location.href, so the user saw flicker / partial
//      page loads / sometimes a refresh loop.
//   2. The redirect did not include a reason, so the user could not tell
//      whether their session expired, the network blipped, or the backend
//      revoked the token.
// Fix: a module-level guard flag so only the first 401 redirects; a small
// localStorage marker so the login screen can show "your session expired"
// instead of a blank login form.

let redirectingToLogin = false;

const PUBLIC_PATHS = new Set([
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
]);

function isPublicPath(pathname) {
  if (PUBLIC_PATHS.has(pathname)) return true;
  // /reset-password/<token>/ etc.
  return pathname.startsWith('/reset-password/');
}

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;

    if (status === 401) {
      const onPublic = isPublicPath(window.location.pathname);
      const hadToken = !!localStorage.getItem('terraMindToken');
      // Always drop the (now-invalid) token so subsequent requests don't
      // resend it. Doing it once is fine because localStorage is shared.
      localStorage.removeItem('terraMindToken');

      if (!onPublic && !redirectingToLogin) {
        redirectingToLogin = true;
        // Surface a reason on the login screen — without it the user sees
        // the form blank and assumes the app is buggy.
        const reason = hadToken ? 'session_expired' : 'auth_required';
        try {
          localStorage.setItem('terraMindLoginReason', reason);
        } catch (_) {
          // localStorage can throw in private browsing modes; not worth
          // failing the redirect over.
        }
        window.location.href = '/login';
      }
    } else if (!status || (status !== 403 && status !== 404)) {
      // Network errors (status undefined) or unexpected HTTP errors.
      // 403/404 are expected app-level flows — never log them as errors.
      // eslint-disable-next-line no-console
      console.error(
        '[API Error]',
        error?.config?.url,
        status ?? 'network',
        error?.message,
      );
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
